import { useEffect, useRef, useState } from "react"

import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import { Loader2, Sparkles } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Separator } from "@/components/ui/separator"
import { Textarea } from "@/components/ui/textarea"
import { type Project, ProjectService } from "@/services/project-service"
import { useAISettingsStore } from "@/stores/ai-settings-store"

import {
  countUnicodeCodePoints,
  MAX_PROJECT_INSTRUCTIONS_CODE_POINTS,
  truncateToUnicodeCodePoints,
} from "./project-instructions-utils"

type ProjectGeneralSettingsProps = {
  project: Project | undefined
  projectId: string
  sessionType: string
  canEdit?: boolean
}

export function ProjectGeneralSettings({
  project,
  projectId,
  sessionType,
  canEdit = true,
}: ProjectGeneralSettingsProps) {
  const queryClient = useQueryClient()
  const { t } = useTranslation("", { keyPrefix: "project_settings.general" })
  const [instructions, setInstructions] = useState(project?.instructions ?? "")
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const isOptimizing = useRef(false)

  const updateProject = useMutation({
    scope: { id: `project-update:${projectId}` },
    mutationFn: (data: {
      name?: string
      instructions?: string
      defaultWebSearch?: boolean
      defaultModelId?: string
      trustedSourceUrls?: string[]
    }) => ProjectService.updateProject(projectId, data),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["projects", sessionType] }),
    onError: () => toast.error(t("save_error")),
  })

  const optimizeInstructions = useMutation({
    mutationFn: (text: string) => ProjectService.optimizeInstructions(text),
    onMutate: () => {
      isOptimizing.current = true
    },
    onSuccess: async (data) => {
      try {
        const optimizedInstructions = data.optimized_instructions
        if (countUnicodeCodePoints(optimizedInstructions) > MAX_PROJECT_INSTRUCTIONS_CODE_POINTS) {
          toast.error(t("instructions_too_long", { max: MAX_PROJECT_INSTRUCTIONS_CODE_POINTS }))
          return
        }

        try {
          await updateProject.mutateAsync({ instructions: optimizedInstructions })
        } catch {
          return
        }

        setInstructions(optimizedInstructions)
        toast.success(t("optimize_success"))
      } finally {
        isOptimizing.current = false
      }
    },
    onError: () => {
      toast.error(t("optimize_error"))
      isOptimizing.current = false
    },
  })

  const initSettings = useAISettingsStore((state) => state.initSettings)
  const availableGptModels = useAISettingsStore(
    (state) => state.availableSettings.availableGptModels
  )

  // Sync local state when project changes
  useEffect(() => {
    setInstructions(project?.instructions ?? "")
  }, [project?.instructions])

  useEffect(() => {
    initSettings(sessionType)
  }, [initSettings, sessionType])

  useEffect(() => {
    const textarea = textareaRef.current
    if (!textarea) return
    textarea.style.height = "auto"
    textarea.style.height = `${textarea.scrollHeight}px`
  }, [instructions])

  return (
    <div className="space-y-6">
      <div className="space-y-3">
        <Label className="text-sm font-medium" htmlFor="project-name">
          {t("project_name")}
        </Label>
        <Input
          className="h-9"
          defaultValue={project?.name || projectId}
          disabled={!canEdit}
          id="project-name"
          placeholder={t("project_name_placeholder")}
          onBlur={(e) => canEdit && updateProject.mutate({ name: e.target.value })}
        />
        <p className="text-muted-foreground text-xs">{t("project_name_description")}</p>
      </div>

      <Separator />

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label className="text-sm font-medium" htmlFor="instructions">
            {t("instructions")}
          </Label>
          <Button
            className="gap-1.5"
            disabled={!canEdit || !instructions.trim() || optimizeInstructions.isPending}
            size="sm"
            variant="outline"
            aria-label={
              optimizeInstructions.isPending
                ? t("optimizing") + " " + t("instructions")
                : t("optimize_prompt") + " " + t("instructions")
            }
            onClick={() => optimizeInstructions.mutate(instructions)}
          >
            {optimizeInstructions.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Sparkles className="h-3.5 w-3.5" />
            )}
            {optimizeInstructions.isPending ? t("optimizing") : t("optimize_prompt")}
          </Button>
        </div>
        <Textarea
          ref={textareaRef}
          className="max-h-[200px] min-h-[100px] resize-none overflow-y-auto md:max-h-[300px]"
          disabled={!canEdit || optimizeInstructions.isPending}
          id="instructions"
          placeholder={t("instructions_placeholder")}
          value={instructions}
          onBlur={() => {
            if (canEdit && !isOptimizing.current && instructions !== project?.instructions) {
              if (countUnicodeCodePoints(instructions) > MAX_PROJECT_INSTRUCTIONS_CODE_POINTS) {
                toast.error(
                  t("instructions_edit_too_long", {
                    max: MAX_PROJECT_INSTRUCTIONS_CODE_POINTS,
                  })
                )
                return
              }
              updateProject.mutate({ instructions })
            }
          }}
          onChange={(e) => {
            const nextInstructions = e.target.value
            const currentCount = countUnicodeCodePoints(instructions)
            const nextCount = countUnicodeCodePoints(nextInstructions)

            setInstructions(
              currentCount > MAX_PROJECT_INSTRUCTIONS_CODE_POINTS && nextCount <= currentCount
                ? nextInstructions
                : truncateToUnicodeCodePoints(nextInstructions)
            )
          }}
        />
        <div className="text-muted-foreground flex items-start justify-between gap-3 text-xs">
          <p>{t("instructions_description")}</p>
          <p aria-live="polite" className="shrink-0">
            {t("instructions_count", {
              count: countUnicodeCodePoints(instructions),
              max: MAX_PROJECT_INSTRUCTIONS_CODE_POINTS,
            })}
          </p>
        </div>
      </div>

      <Separator />

      <div className="space-y-3">
        <Label className="text-sm font-medium" htmlFor="default-model">
          {t("default_ai_model")}
        </Label>
        <Select
          disabled={!canEdit}
          value={project?.defaultModelId ?? ""}
          onValueChange={(v) => canEdit && updateProject.mutate({ defaultModelId: v || undefined })}
        >
          <SelectTrigger className="h-9" id="default-model">
            <SelectValue placeholder={t("select_model_placeholder")} />
          </SelectTrigger>
          <SelectContent>
            {availableGptModels.map((model) => (
              <SelectItem key={model.id} value={model.id}>
                <div className="flex flex-col items-start">
                  <span className="font-medium">{model.display_name || model.id}</span>
                  {model.description && (
                    <span className="text-muted-foreground text-xs">{model.description}</span>
                  )}
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-muted-foreground text-xs">{t("model_description")}</p>
      </div>
    </div>
  )
}
